import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { useAuth, API } from "../App";
import { toast } from "sonner";
import { useAtencionPrioritaria } from "../contexts/AtencionPrioritariaContext";
import Sidebar from "../components/Sidebar";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Checkbox } from "../components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "../components/ui/select";
import {
  Users,
  Plus,
  Search,
  Edit2,
  Trash2,
  Loader2,
  Phone,
  Smartphone,
  MapPin,
  FileSpreadsheet,
  Upload,
  ArrowRight,
  CheckCircle,
  AlertTriangle,
  Building2,
  Star,
  Receipt,
  Mail,
  Send
} from "lucide-react";
import { useFacturaMismoMes } from "../contexts/FacturaMismoMesContext";

// Opciones de sucursales
const SUCURSALES_OPTIONS = [
  { value: "N/A", label: "N/A (Sin sucursal)" },
  { value: "Sucursal Constanza", label: "Sucursal Constanza" },
  { value: "Sucursal Terrenas", label: "Sucursal Terrenas" },
  { value: "Sucursal Bavaro", label: "Sucursal Bavaro" }
];

export default function ClientesMensajeriaPage() {
  const { esPrioritario, getRecord, marcar, desmarcar, puedeMarcar } = useAtencionPrioritaria();
  const { esMarcado: esFacturaMes, getRecord: getFacturaRecord, marcar: marcarFactura, desmarcar: desmarcarFactura, puedeMarcar: puedeMarcarFactura } = useFacturaMismoMes();
  const [prioDialog, setPrioDialog] = useState(null); // cliente en edición de prioridad
  const [prioMotivo, setPrioMotivo] = useState("");
  const [factDialog, setFactDialog] = useState(null); // cliente en edición de Factura Mismo Mes
  const [factMotivo, setFactMotivo] = useState("");
  const [factSaving, setFactSaving] = useState(false);
  // Resumen semanal a supervisores (solo admin)
  const [showResumenConfig, setShowResumenConfig] = useState(false);
  const [resumenCfg, setResumenCfg] = useState({ activo: false, dia: 0, hora: "08:00" });
  const [resumenSaving, setResumenSaving] = useState(false);
  const [resumenSending, setResumenSending] = useState(false);
  const [prioSaving, setPrioSaving] = useState(false);
  const [clientes, setClientes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [showDialog, setShowDialog] = useState(false);
  const [editingCliente, setEditingCliente] = useState(null);
  const [formData, setFormData] = useState({
    nombre: "",
    direccion: "",
    telefono: "",
    celular: "",
    sucursal: "N/A"
  });
  
  // Bulk delete states
  const [selectedClientes, setSelectedClientes] = useState([]);
  const [deletingBulk, setDeletingBulk] = useState(false);
  
  // Bulk import states
  const [showBulkDialog, setShowBulkDialog] = useState(false);
  const [bulkMode, setBulkMode] = useState("upsert");
  const [bulkStep, setBulkStep] = useState(1);
  const [bulkLoading, setBulkLoading] = useState(false);
  const [bulkPreview, setBulkPreview] = useState(null);
  const [bulkMapping, setBulkMapping] = useState({});
  const [bulkResult, setBulkResult] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const bulkFileInputRef = useRef(null);
  
  const { getAuthHeaders, user } = useAuth();
  const isAdmin = ["admin", "administrador"].includes((user?.role || "").toLowerCase());

  const fetchClientes = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/mensajeria/clientes`, getAuthHeaders());
      setClientes(res.data);
    } catch (err) {
      console.error("Error:", err);
      toast.error("Error al cargar clientes");
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    fetchClientes();
  }, [fetchClientes]);

  const resetForm = () => {
    setFormData({
      nombre: "",
      direccion: "",
      telefono: "",
      celular: "",
      sucursal: "N/A"
    });
    setEditingCliente(null);
  };

  const openNewDialog = () => {
    resetForm();
    setShowDialog(true);
  };

  const openEditDialog = (cliente) => {
    setEditingCliente(cliente);
    setFormData({
      nombre: cliente.nombre,
      direccion: cliente.direccion || "",
      telefono: cliente.telefono || "",
      celular: cliente.celular || "",
      sucursal: cliente.sucursal || "N/A"
    });
    setShowDialog(true);
  };

  const handleTogglePrioridad = (cliente) => {
    if (esPrioritario(cliente.nombre)) {
      // Quitar directamente
      desmarcar(cliente.nombre)
        .then(() => toast.success(`Atención Prioritaria removida de ${cliente.nombre}`))
        .catch((e) => toast.error(e?.response?.data?.detail || "No se pudo quitar"));
    } else {
      const rec = getRecord(cliente.nombre);
      setPrioMotivo(rec?.motivo || "");
      setPrioDialog(cliente);
    }
  };

  const confirmarPrioridad = async () => {
    if (!prioDialog) return;
    setPrioSaving(true);
    try {
      await marcar(prioDialog.nombre, prioMotivo);
      toast.success(`${prioDialog.nombre} marcado como Atención Prioritaria`);
      setPrioDialog(null);
      setPrioMotivo("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo marcar");
    } finally {
      setPrioSaving(false);
    }
  };


  const handleToggleFacturaMes = (cliente) => {
    if (esFacturaMes(cliente.nombre)) {
      desmarcarFactura(cliente.nombre)
        .then(() => toast.success(`Factura Mismo Mes removida de ${cliente.nombre}`))
        .catch((e) => toast.error(e?.response?.data?.detail || "No se pudo quitar"));
    } else {
      const rec = getFacturaRecord(cliente.nombre);
      setFactMotivo(rec?.motivo || "");
      setFactDialog(cliente);
    }
  };

  const confirmarFacturaMes = async () => {
    if (!factDialog) return;
    setFactSaving(true);
    try {
      await marcarFactura(factDialog.nombre, factMotivo);
      toast.success(`${factDialog.nombre} marcado como Factura Mismo Mes`);
      setFactDialog(null);
      setFactMotivo("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo marcar");
    } finally {
      setFactSaving(false);
    }
  };


  const openResumenConfig = async () => {
    try {
      const res = await axios.get(`${API}/factura-mismo-mes/config-resumen`, getAuthHeaders());
      setResumenCfg({ activo: !!res.data.activo, dia: res.data.dia ?? 0, hora: res.data.hora || "08:00" });
    } catch (e) { /* solo admin */ }
    setShowResumenConfig(true);
  };

  const saveResumenConfig = async () => {
    setResumenSaving(true);
    try {
      await axios.put(`${API}/factura-mismo-mes/config-resumen`, resumenCfg, getAuthHeaders());
      toast.success("Configuración guardada");
      setShowResumenConfig(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al guardar");
    } finally { setResumenSaving(false); }
  };

  const enviarResumenAhora = async () => {
    setResumenSending(true);
    try {
      const res = await axios.post(`${API}/factura-mismo-mes/config-resumen/enviar-ahora`, {}, getAuthHeaders());
      toast.success(res.data.message || "Resumen enviado", { duration: 6000 });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al enviar");
    } finally { setResumenSending(false); }
  };

  const handleSubmit = async () => {
    if (!formData.nombre.trim()) {
      toast.error("El nombre del cliente es requerido");
      return;
    }

    try {
      if (editingCliente) {
        await axios.put(
          `${API}/mensajeria/clientes/${editingCliente.id}`,
          formData,
          getAuthHeaders()
        );
        toast.success("Cliente actualizado");
      } else {
        await axios.post(`${API}/mensajeria/clientes`, formData, getAuthHeaders());
        toast.success("Cliente creado");
      }
      setShowDialog(false);
      resetForm();
      fetchClientes();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al guardar");
    }
  };

  const deleteCliente = async (id) => {
    if (!window.confirm("¿Está seguro de eliminar este cliente?")) return;
    
    try {
      await axios.delete(`${API}/mensajeria/clientes/${id}`, getAuthHeaders());
      toast.success("Cliente eliminado");
      fetchClientes();
    } catch (err) {
      toast.error("Error al eliminar cliente");
    }
  };

  // Bulk delete functions
  const toggleSelectCliente = (id) => {
    setSelectedClientes(prev => 
      prev.includes(id) 
        ? prev.filter(cId => cId !== id)
        : [...prev, id]
    );
  };

  const toggleSelectAll = () => {
    if (selectedClientes.length === filteredClientes.length) {
      setSelectedClientes([]);
    } else {
      setSelectedClientes(filteredClientes.map(c => c.id));
    }
  };

  const deleteBulkClientes = async () => {
    if (selectedClientes.length === 0) return;
    
    if (!window.confirm(`¿Está seguro de eliminar ${selectedClientes.length} cliente(s)?`)) return;
    
    setDeletingBulk(true);
    try {
      await axios.post(
        `${API}/mensajeria/clientes/bulk-delete`,
        { ids: selectedClientes },
        getAuthHeaders()
      );
      toast.success(`${selectedClientes.length} cliente(s) eliminado(s)`);
      setSelectedClientes([]);
      fetchClientes();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al eliminar clientes");
    } finally {
      setDeletingBulk(false);
    }
  };

  // Bulk Import Functions
  const openBulkDialog = () => {
    setBulkStep(1);
    setBulkPreview(null);
    setBulkMapping({});
    setBulkResult(null);
    setSelectedFile(null);
    setBulkMode("upsert");
    setIsDragging(false);
    setShowBulkDialog(true);
  };

  const processFile = async (file) => {
    if (!file) return;
    
    // Validate file type
    if (!file.name.endsWith('.xlsx') && !file.name.endsWith('.xls')) {
      toast.error("Solo se permiten archivos Excel (.xlsx, .xls)");
      return;
    }

    setSelectedFile(file);
    setBulkLoading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const res = await axios.post(
        `${API}/mensajeria/clientes/bulk-import/preview`,
        formData,
        {
          ...getAuthHeaders(),
          headers: {
            ...getAuthHeaders().headers,
            'Content-Type': 'multipart/form-data'
          }
        }
      );
      setBulkPreview(res.data);
      setBulkMapping(res.data.suggested_mapping || {});
      setBulkStep(2);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al procesar archivo");
      setSelectedFile(null);
    } finally {
      setBulkLoading(false);
    }
  };

  const handleBulkFileSelect = async (e) => {
    const file = e.target.files[0];
    await processFile(file);
  };

  // Drag and drop handlers
  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    
    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      await processFile(files[0]);
    }
  };

  const executeBulkImport = async () => {
    if (!selectedFile) {
      toast.error("No hay archivo seleccionado");
      return;
    }
    
    setBulkLoading(true);
    const formData = new FormData();
    formData.append('file', selectedFile);
    formData.append('mapping', JSON.stringify(bulkMapping));
    formData.append('update_mode', bulkMode);

    try {
      const res = await axios.post(
        `${API}/mensajeria/clientes/bulk-import/execute`,
        formData,
        {
          ...getAuthHeaders(),
          headers: {
            ...getAuthHeaders().headers,
            'Content-Type': 'multipart/form-data'
          }
        }
      );
      setBulkResult(res.data);
      setBulkStep(3);
      fetchClientes();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al importar");
    } finally {
      setBulkLoading(false);
    }
  };

  const filteredClientes = clientes.filter(c =>
    c.nombre.toLowerCase().includes(searchQuery.toLowerCase()) ||
    (c.direccion || "").toLowerCase().includes(searchQuery.toLowerCase()) ||
    (c.telefono || "").includes(searchQuery) ||
    (c.celular || "").includes(searchQuery)
  );

  return (
    <div className="flex min-h-screen bg-slate-50 dark:bg-slate-900">
      <Sidebar />
      
      <main className="flex-1 ml-0 md:ml-64 p-4 md:p-8 pt-14 md:pt-8 overflow-x-hidden" data-testid="clientes-mensajeria-page">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-blue-600 to-cyan-700 flex items-center justify-center shadow-lg">
              <Users className="w-7 h-7 text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-slate-900 dark:text-white font-['Manrope']">
                Clientes de Mensajería
              </h1>
              <p className="text-slate-500 dark:text-slate-400 mt-1">
                Gestiona la base de datos de clientes para correspondencias
              </p>
            </div>
          </div>
          <div className="flex gap-3">
            {isAdmin && (
              <Button
                onClick={openResumenConfig}
                variant="outline"
                className="border-[#0C314C] text-[#0C314C] hover:bg-slate-50"
                data-testid="resumen-supervisores-btn"
              >
                <Mail className="w-4 h-4 mr-2" />
                Resumen a Supervisores
              </Button>
            )}
            <Button 
              onClick={openBulkDialog}
              variant="outline"
              className="border-blue-600 text-blue-600 hover:bg-blue-50"
              data-testid="bulk-import-btn"
            >
              <FileSpreadsheet className="w-4 h-4 mr-2" />
              Importar Excel
            </Button>
            <Button 
              onClick={openNewDialog}
              className="bg-blue-600 hover:bg-blue-700 text-white"
              data-testid="new-cliente-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Nuevo Cliente
            </Button>
          </div>
        </div>

        {/* Search */}
        <div className="mb-6">
          <div className="relative max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Buscar por nombre, dirección, teléfono..."
              className="pl-10"
              data-testid="search-clientes"
            />
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                  <Users className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">{clientes.length}</p>
                  <p className="text-xs text-slate-500">Total Clientes</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                  <Phone className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">
                    {clientes.filter(c => c.telefono || c.celular).length}
                  </p>
                  <p className="text-xs text-slate-500">Con Contacto</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                  <Building2 className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">
                    {clientes.filter(c => c.sucursal && c.sucursal !== "N/A").length}
                  </p>
                  <p className="text-xs text-slate-500">Con Sucursal</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* List */}
        <Card>
          <CardHeader className="border-b">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                {filteredClientes.length > 0 && (
                  <Checkbox
                    checked={selectedClientes.length === filteredClientes.length && filteredClientes.length > 0}
                    onCheckedChange={toggleSelectAll}
                    data-testid="select-all-checkbox"
                  />
                )}
                <CardTitle>Clientes ({filteredClientes.length})</CardTitle>
                {selectedClientes.length > 0 && (
                  <Badge variant="secondary" className="bg-blue-100 text-blue-700">
                    {selectedClientes.length} seleccionado(s)
                  </Badge>
                )}
              </div>
              {selectedClientes.length > 0 && (
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={deleteBulkClientes}
                  disabled={deletingBulk}
                  data-testid="bulk-delete-btn"
                >
                  {deletingBulk ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Trash2 className="w-4 h-4 mr-2" />
                  )}
                  Eliminar ({selectedClientes.length})
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
              </div>
            ) : filteredClientes.length === 0 ? (
              <div className="text-center py-12 text-slate-500">
                {searchQuery ? "No se encontraron clientes" : "No hay clientes registrados"}
              </div>
            ) : (
              <div className="divide-y">
                {filteredClientes.map((cliente) => (
                  <div
                    key={cliente.id}
                    className={`flex items-center justify-between p-4 hover:bg-slate-50 transition-colors ${selectedClientes.includes(cliente.id) ? 'bg-blue-50' : ''}`}
                    data-testid={`cliente-row-${cliente.id}`}
                  >
                    <div className="flex items-center gap-4">
                      <Checkbox
                        checked={selectedClientes.includes(cliente.id)}
                        onCheckedChange={() => toggleSelectCliente(cliente.id)}
                        data-testid={`select-cliente-${cliente.id}`}
                      />
                      <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                        <span className="text-blue-600 font-semibold">
                          {cliente.nombre.charAt(0).toUpperCase()}
                        </span>
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <p className="font-medium text-slate-900">{cliente.nombre}</p>
                          {esPrioritario(cliente.nombre) && (
                            <Badge className="text-xs bg-red-100 text-red-700 border border-red-300" data-testid={`prio-badge-${cliente.id}`}>
                              <AlertTriangle className="w-3 h-3 mr-1" />
                              Atención Prioritaria
                            </Badge>
                          )}
                          {esFacturaMes(cliente.nombre) && (
                            <Badge className="text-xs bg-blue-100 text-blue-700 border border-blue-300" data-testid={`factura-mes-badge-${cliente.id}`}>
                              <Receipt className="w-3 h-3 mr-1" />
                              Factura Mismo Mes
                            </Badge>
                          )}
                          {cliente.sucursal && cliente.sucursal !== "N/A" && (
                            <Badge variant="secondary" className="text-xs bg-purple-100 text-purple-700">
                              <Building2 className="w-3 h-3 mr-1" />
                              {cliente.sucursal}
                            </Badge>
                          )}
                        </div>
                        {cliente.direccion && (
                          <p className="text-sm text-slate-500 flex items-center gap-1">
                            <MapPin className="w-3 h-3" />
                            {cliente.direccion}
                          </p>
                        )}
                        <div className="flex items-center gap-4 mt-1">
                          {cliente.telefono && (
                            <span className="text-xs text-slate-500 flex items-center gap-1">
                              <Phone className="w-3 h-3" />
                              {cliente.telefono}
                            </span>
                          )}
                          {cliente.celular && (
                            <span className="text-xs text-slate-500 flex items-center gap-1">
                              <Smartphone className="w-3 h-3" />
                              {cliente.celular}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {puedeMarcar && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleTogglePrioridad(cliente)}
                          className={esPrioritario(cliente.nombre)
                            ? "border-red-300 text-red-600 bg-red-50 hover:bg-red-100"
                            : "border-amber-200 text-amber-600 hover:bg-amber-50"}
                          title={esPrioritario(cliente.nombre) ? "Quitar Atención Prioritaria" : "Marcar Atención Prioritaria"}
                          data-testid={`prio-toggle-${cliente.id}`}
                        >
                          <Star className={`w-4 h-4 ${esPrioritario(cliente.nombre) ? "fill-current" : ""}`} />
                        </Button>
                      )}
                      {puedeMarcarFactura && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleToggleFacturaMes(cliente)}
                          className={esFacturaMes(cliente.nombre)
                            ? "border-blue-300 text-blue-600 bg-blue-50 hover:bg-blue-100"
                            : "border-sky-200 text-sky-600 hover:bg-sky-50"}
                          title={esFacturaMes(cliente.nombre) ? "Quitar Factura Mismo Mes" : "Marcar Factura Mismo Mes"}
                          data-testid={`factura-mes-toggle-${cliente.id}`}
                        >
                          <Receipt className="w-4 h-4" />
                        </Button>
                      )}
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => openEditDialog(cliente)}
                        data-testid={`edit-cliente-${cliente.id}`}
                      >
                        <Edit2 className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => deleteCliente(cliente.id)}
                        className="border-red-200 text-red-600 hover:bg-red-50"
                        data-testid={`delete-cliente-${cliente.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Diálogo: marcar Atención Prioritaria con motivo */}
        <Dialog open={!!prioDialog} onOpenChange={(o) => { if (!o) { setPrioDialog(null); setPrioMotivo(""); } }}>
          <DialogContent className="max-w-md" data-testid="prio-motivo-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-red-700">
                <AlertTriangle className="w-5 h-5" />
                Marcar Atención Prioritaria
              </DialogTitle>
              <DialogDescription>
                Se activará una alerta en todos los módulos al trabajar con <strong>{prioDialog?.nombre}</strong>.
              </DialogDescription>
            </DialogHeader>
            <div className="py-2 space-y-2">
              <Label htmlFor="prio-motivo">Motivo (opcional)</Label>
              <Input
                id="prio-motivo"
                value={prioMotivo}
                onChange={(e) => setPrioMotivo(e.target.value)}
                placeholder="Ej. Renovación urgente, cliente VIP, reclamo abierto…"
                data-testid="prio-motivo-input"
              />
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => { setPrioDialog(null); setPrioMotivo(""); }}>Cancelar</Button>
              <Button
                className="bg-red-600 hover:bg-red-700"
                onClick={confirmarPrioridad}
                disabled={prioSaving}
                data-testid="prio-motivo-confirm"
              >
                {prioSaving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Star className="w-4 h-4 mr-2" />}
                Marcar
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Diálogo: marcar Factura Mismo Mes con motivo */}
        <Dialog open={!!factDialog} onOpenChange={(o) => { if (!o) { setFactDialog(null); setFactMotivo(""); } }}>
          <DialogContent className="max-w-md" data-testid="factura-mes-motivo-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-blue-700">
                <Receipt className="w-5 h-5" />
                Marcar Factura Mismo Mes
              </DialogTitle>
              <DialogDescription>
                Cuando una renovación de <strong>{factDialog?.nombre}</strong> esté en status RECIBIDA,
                el ejecutivo recibirá 2 alertas diarias (10:30 AM y 3:00 PM) hasta que esté
                RENOVADA con confirmación de recibido.
              </DialogDescription>
            </DialogHeader>
            <div className="py-2 space-y-2">
              <Label htmlFor="factura-mes-motivo">Motivo (opcional)</Label>
              <Input
                id="factura-mes-motivo"
                value={factMotivo}
                onChange={(e) => setFactMotivo(e.target.value)}
                placeholder="Ej. Cliente requiere factura dentro del mes de emisión…"
                data-testid="factura-mes-motivo-input"
              />
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => { setFactDialog(null); setFactMotivo(""); }}>Cancelar</Button>
              <Button
                className="bg-blue-600 hover:bg-blue-700"
                onClick={confirmarFacturaMes}
                disabled={factSaving}
                data-testid="factura-mes-motivo-confirm"
              >
                {factSaving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Receipt className="w-4 h-4 mr-2" />}
                Marcar
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Diálogo: configuración Resumen a Supervisores (solo admin) */}
        <Dialog open={showResumenConfig} onOpenChange={setShowResumenConfig}>
          <DialogContent className="max-w-md" data-testid="resumen-supervisores-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-[#0C314C]">
                <Mail className="w-5 h-5" />
                Resumen a Supervisores — Facturas Mismo Mes
              </DialogTitle>
              <DialogDescription>
                Envía a cada supervisor un resumen semanal con las renovaciones de clientes
                marcados "Factura Mismo Mes" que sigan pendientes de entrega.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-2">
              <div className="flex items-center gap-3">
                <input type="checkbox" id="resumen-activo" checked={resumenCfg.activo}
                  onChange={(e) => setResumenCfg({ ...resumenCfg, activo: e.target.checked })}
                  data-testid="resumen-config-activo" />
                <Label htmlFor="resumen-activo">Activar envío semanal automático</Label>
              </div>
              <div>
                <Label>Día de envío</Label>
                <div className="flex flex-wrap gap-2 mt-1">
                  {["Lun", "Mar", "Mié", "Jue", "Vie", "Sáb", "Dom"].map((l, v) => (
                    <button key={v} type="button"
                      onClick={() => setResumenCfg({ ...resumenCfg, dia: v })}
                      className={`px-3 py-1 rounded-full text-sm border ${resumenCfg.dia === v ? "bg-[#0C314C] text-white border-[#0C314C]" : "border-slate-300 text-slate-600"}`}
                      data-testid={`resumen-config-dia-${v}`}>{l}</button>
                  ))}
                </div>
              </div>
              <div className="w-40">
                <Label>Hora (RD)</Label>
                <Input type="time" value={resumenCfg.hora}
                  onChange={(e) => setResumenCfg({ ...resumenCfg, hora: e.target.value })}
                  data-testid="resumen-config-hora" />
              </div>
            </div>
            <DialogFooter className="flex-col sm:flex-row gap-2">
              <Button variant="outline" onClick={enviarResumenAhora} disabled={resumenSending} data-testid="resumen-enviar-ahora-btn">
                {resumenSending ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
                Enviar ahora
              </Button>
              <Button className="bg-[#0C314C] hover:bg-[#2a4d7a] text-white" onClick={saveResumenConfig}
                disabled={resumenSaving} data-testid="resumen-save-config-btn">
                {resumenSaving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                Guardar Configuración
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <Dialog open={showDialog} onOpenChange={setShowDialog}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>
                {editingCliente ? "Editar Cliente" : "Nuevo Cliente"}
              </DialogTitle>
              <DialogDescription>
                Complete los datos del cliente de mensajería
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Nombre del Cliente *</Label>
                <Input
                  value={formData.nombre}
                  onChange={(e) => setFormData({...formData, nombre: e.target.value})}
                  placeholder="Nombre completo o empresa"
                  data-testid="input-nombre"
                />
              </div>
              
              <div className="space-y-2">
                <Label>Dirección</Label>
                <Input
                  value={formData.direccion}
                  onChange={(e) => setFormData({...formData, direccion: e.target.value})}
                  placeholder="Dirección completa"
                  data-testid="input-direccion"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Teléfono</Label>
                  <Input
                    value={formData.telefono}
                    onChange={(e) => setFormData({...formData, telefono: e.target.value})}
                    placeholder="809-000-0000"
                    data-testid="input-telefono"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Celular</Label>
                  <Input
                    value={formData.celular}
                    onChange={(e) => setFormData({...formData, celular: e.target.value})}
                    placeholder="809-000-0000"
                    data-testid="input-celular"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <Building2 className="w-4 h-4" />
                  Sucursal
                </Label>
                <Select
                  value={formData.sucursal}
                  onValueChange={(value) => setFormData({...formData, sucursal: value})}
                >
                  <SelectTrigger data-testid="select-sucursal">
                    <SelectValue placeholder="Seleccionar sucursal" />
                  </SelectTrigger>
                  <SelectContent>
                    {SUCURSALES_OPTIONS.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setShowDialog(false)}>
                Cancelar
              </Button>
              <Button onClick={handleSubmit} className="bg-blue-600 hover:bg-blue-700">
                {editingCliente ? "Guardar Cambios" : "Crear Cliente"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Bulk Import Dialog */}
        <Dialog open={showBulkDialog} onOpenChange={setShowBulkDialog}>
          <DialogContent className="max-w-3xl max-h-[85vh] flex flex-col">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <FileSpreadsheet className="w-5 h-5 text-blue-600" />
                Importar Clientes desde Excel
              </DialogTitle>
              <DialogDescription>
                {bulkStep === 1 && "Sube un archivo Excel (.xlsx) con los datos de los clientes"}
                {bulkStep === 2 && "Verifica y ajusta el mapeo de columnas"}
                {bulkStep === 3 && "Resultado de la importación"}
              </DialogDescription>
            </DialogHeader>

            {/* Progress Steps */}
            <div className="flex items-center justify-center gap-2 py-4 border-b">
              <div className={`flex items-center gap-2 px-3 py-1 rounded-full text-sm ${bulkStep >= 1 ? 'bg-blue-600 text-white' : 'bg-slate-200 text-slate-500'}`}>
                <span className="w-5 h-5 rounded-full bg-white/20 flex items-center justify-center text-xs">1</span>
                Subir
              </div>
              <ArrowRight className="w-4 h-4 text-slate-400" />
              <div className={`flex items-center gap-2 px-3 py-1 rounded-full text-sm ${bulkStep >= 2 ? 'bg-blue-600 text-white' : 'bg-slate-200 text-slate-500'}`}>
                <span className="w-5 h-5 rounded-full bg-white/20 flex items-center justify-center text-xs">2</span>
                Mapear
              </div>
              <ArrowRight className="w-4 h-4 text-slate-400" />
              <div className={`flex items-center gap-2 px-3 py-1 rounded-full text-sm ${bulkStep >= 3 ? 'bg-blue-600 text-white' : 'bg-slate-200 text-slate-500'}`}>
                <span className="w-5 h-5 rounded-full bg-white/20 flex items-center justify-center text-xs">3</span>
                Resultado
              </div>
            </div>

            <div className="flex-1 overflow-y-auto py-4">
              {/* Step 1: Upload */}
              {bulkStep === 1 && (
                <div className="flex flex-col items-center justify-center py-12">
                  <input
                    type="file"
                    ref={bulkFileInputRef}
                    accept=".xlsx,.xls"
                    onChange={handleBulkFileSelect}
                    className="hidden"
                    data-testid="bulk-file-input"
                  />
                  <div 
                    className={`border-2 border-dashed rounded-xl p-12 text-center cursor-pointer transition-all duration-200 w-full max-w-lg
                      ${isDragging 
                        ? 'border-blue-500 bg-blue-50 scale-105' 
                        : 'border-slate-300 hover:border-blue-500 hover:bg-blue-50/50'
                      }`}
                    onClick={() => bulkFileInputRef.current?.click()}
                    onDragEnter={handleDragEnter}
                    onDragLeave={handleDragLeave}
                    onDragOver={handleDragOver}
                    onDrop={handleDrop}
                    data-testid="bulk-dropzone"
                  >
                    {bulkLoading ? (
                      <Loader2 className="w-12 h-12 text-blue-600 animate-spin mx-auto mb-4" />
                    ) : (
                      <Upload className={`w-12 h-12 mx-auto mb-4 transition-colors ${isDragging ? 'text-blue-600' : 'text-slate-400'}`} />
                    )}
                    <p className={`text-lg font-medium ${isDragging ? 'text-blue-700' : 'text-slate-700'}`}>
                      {bulkLoading 
                        ? "Procesando archivo..." 
                        : isDragging 
                          ? "Suelta el archivo aquí" 
                          : "Arrastra un archivo Excel aquí"
                      }
                    </p>
                    <p className="text-sm text-slate-500 mt-2">
                      {!isDragging && !bulkLoading && "o haz clic para seleccionar"}
                    </p>
                    <p className="text-xs text-slate-400 mt-4">
                      Formatos aceptados: .xlsx, .xls
                    </p>
                  </div>
                </div>
              )}

              {/* Step 2: Mapping */}
              {bulkStep === 2 && bulkPreview && (
                <div className="space-y-6">
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <p className="text-sm text-blue-800">
                      <strong>{bulkPreview.total_rows}</strong> filas encontradas en el archivo.
                      Mapea las columnas del Excel a los campos del sistema.
                    </p>
                  </div>

                  {/* Modo de importación */}
                  <div className="space-y-2" data-testid="bulk-mode-selector">
                    <h4 className="font-medium text-slate-900">¿Qué hacer con los contactos?</h4>
                    <div className="grid grid-cols-1 gap-2">
                      {[
                        { value: "upsert", titulo: "Actualizar existentes y crear nuevos", desc: "Si el contacto ya existe (mismo nombre) actualiza su dirección, teléfono, etc. Si no existe, lo crea. (Recomendado)" },
                        { value: "solo_actualizar", titulo: "Solo actualizar existentes", desc: "Actualiza datos de los contactos que ya existen. NO crea contactos nuevos." },
                        { value: "solo_crear", titulo: "Solo crear nuevos", desc: "Crea únicamente los contactos que no existen. NO modifica los existentes." },
                      ].map((opt) => (
                        <label
                          key={opt.value}
                          className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${bulkMode === opt.value ? "border-blue-500 bg-blue-50" : "border-slate-200 hover:border-slate-300"}`}
                          data-testid={`bulk-mode-${opt.value}`}
                        >
                          <input
                            type="radio"
                            name="bulk-mode"
                            value={opt.value}
                            checked={bulkMode === opt.value}
                            onChange={() => setBulkMode(opt.value)}
                            className="mt-1 accent-blue-600"
                          />
                          <span>
                            <span className="block text-sm font-medium text-slate-800">{opt.titulo}</span>
                            <span className="block text-xs text-slate-500">{opt.desc}</span>
                          </span>
                        </label>
                      ))}
                    </div>
                  </div>

                  <div className="space-y-4">
                    <h4 className="font-medium text-slate-900">Mapeo de Columnas</h4>
                    {bulkPreview.columns.map((col) => (
                      <div key={col} className="flex items-center gap-4">
                        <div className="w-1/3">
                          <Badge variant="outline" className="text-sm">
                            {col}
                          </Badge>
                        </div>
                        <ArrowRight className="w-4 h-4 text-slate-400" />
                        <div className="w-1/3">
                          <Select
                            value={bulkMapping[col] || "_skip"}
                            onValueChange={(value) => setBulkMapping({...bulkMapping, [col]: value === "_skip" ? "" : value})}
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="Seleccionar campo" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="_skip">-- No importar --</SelectItem>
                              {bulkPreview.target_fields.map((field) => (
                                <SelectItem key={field.value} value={field.value}>
                                  {field.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Preview Table */}
                  <div className="mt-6">
                    <h4 className="font-medium text-slate-900 mb-2">Vista Previa (5 primeras filas)</h4>
                    <div className="overflow-x-auto border rounded-lg">
                      <table className="min-w-full text-sm">
                        <thead className="bg-slate-50">
                          <tr>
                            {bulkPreview.columns.map((col) => (
                              <th key={col} className="px-4 py-2 text-left font-medium text-slate-600">
                                {col}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody className="divide-y">
                          {bulkPreview.sample_data.map((row, idx) => (
                            <tr key={idx}>
                              {bulkPreview.columns.map((col) => (
                                <td key={col} className="px-4 py-2 text-slate-700">
                                  {row[col] || "-"}
                                </td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              )}

              {/* Step 3: Result */}
              {bulkStep === 3 && bulkResult && (
                <div className="text-center py-8">
                  <div className={`w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center ${bulkResult.success ? 'bg-green-100' : 'bg-red-100'}`}>
                    {bulkResult.success ? (
                      <CheckCircle className="w-8 h-8 text-green-600" />
                    ) : (
                      <AlertTriangle className="w-8 h-8 text-red-600" />
                    )}
                  </div>
                  <h3 className="text-xl font-bold text-slate-900 mb-2">
                    {bulkResult.success ? "Importación Exitosa" : "Importación Completada con Errores"}
                  </h3>
                  <p className="text-slate-600 mb-4">
                    {typeof bulkResult.actualizados === "number" ? (
                      <>
                        <strong>{bulkResult.creados}</strong> creados,{" "}
                        <strong>{bulkResult.actualizados}</strong> actualizados
                        {bulkResult.omitidos > 0 && <> , <strong>{bulkResult.omitidos}</strong> omitidos</>}.
                      </>
                    ) : (
                      <>Se importaron <strong>{bulkResult.created}</strong> clientes.</>
                    )}
                  </p>
                  {bulkResult.errors.length > 0 && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-left mt-4">
                      <p className="font-medium text-red-800 mb-2">
                        Errores ({bulkResult.total_errors}):
                      </p>
                      <ul className="text-sm text-red-700 space-y-1">
                        {bulkResult.errors.map((err, idx) => (
                          <li key={idx}>• {err}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}
            </div>

            <DialogFooter className="border-t pt-4">
              {bulkStep === 1 && (
                <Button variant="outline" onClick={() => setShowBulkDialog(false)}>
                  Cancelar
                </Button>
              )}
              {bulkStep === 2 && (
                <>
                  <Button variant="outline" onClick={() => setBulkStep(1)}>
                    Atrás
                  </Button>
                  <Button 
                    onClick={executeBulkImport} 
                    disabled={bulkLoading || !Object.values(bulkMapping).some(v => v)}
                    className="bg-blue-600 hover:bg-blue-700"
                  >
                    {bulkLoading ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Importando...
                      </>
                    ) : (
                      "Importar Clientes"
                    )}
                  </Button>
                </>
              )}
              {bulkStep === 3 && (
                <Button onClick={() => setShowBulkDialog(false)}>
                  Cerrar
                </Button>
              )}
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
}
